/**
 * Orçamento de CONTEXTO da conversa — o que cabe numa requisição ao modelo.
 *
 * Funções puras sobre o array `messages`: nenhuma toca Office.js ou DOM, por
 * isso vivem aqui (e não no taskpane) e podem ser exercitadas fora do Word —
 * ver `addin/scripts/check-context.mjs`.
 *
 * Duas responsabilidades:
 *  - PODAR blocos volumosos (`tool_result` de leitura, `input` de reescrita em
 *    massa) que já cumpriram sua função;
 *  - MEDIR o tamanho da conversa para decidir quando podar e quando parar.
 */
/**
 * Poda blocos GRANDES de um turno ao entrar no histórico: conteúdos de
 * `tool_result` (ex.: leituras de documento) e `input` de `tool_use` (ex.:
 * reescritas em massa) acima do teto viram um stub curto.
 *
 * Por quê: numa sessão longa sobre documento grande, cada leitura/reescrita
 * pode ter dezenas de KB — carregá-las verbatim em TODOS os passos de TODOS os
 * turnos seguintes explode o custo de tokens e aproxima a conversa do limite
 * de contexto. E o conteúdo antigo é REDUNDANTE com o próprio documento (a
 * fonte da verdade, releitável a qualquer momento) — pior, fica DEFASADO
 * assim que o documento muda, virando risco de alucinação. O stub instrui a
 * reler em vez de confiar na cópia velha (regra correspondente no system
 * prompt, seção "Documentos GRANDES").
 *
 * Roda só na FRONTEIRA do turno (ao persistir a memória): dentro do turno o
 * modelo mantém as leituras íntegras para agir sobre elas, e o histórico fica
 * estável durante os passos do turno seguinte (bom para o prompt caching). O
 * texto final do assistente (resumo do que foi feito) é preservado — é ele
 * que dá continuidade entre turnos.
 */
export const PRUNE_BLOCK_MAX_CHARS = 1200;
export function pruneBulkyToolBlocks(messages) {
    return messages.map(pruneMessageBlocks);
}
/** Poda os blocos volumosos de UMA mensagem. Base das duas podas (turno e histórico). */
export function pruneMessageBlocks(m) {
    {
        if (!Array.isArray(m.content))
            return m;
        const content = m.content.map((block) => {
            var _a;
            if (block.type === 'tool_result' && typeof block.content === 'string') {
                const len = block.content.length;
                if (len > PRUNE_BLOCK_MAX_CHARS) {
                    return Object.assign(Object.assign({}, block), { content: `[Resultado omitido do histórico (${len} caracteres) para economizar contexto. ` +
                            'Pode estar desatualizado: para o estado ATUAL do documento use ' +
                            'get_document_overview/read_document_text.]' });
                }
            }
            if (block.type === 'tool_use') {
                const size = JSON.stringify((_a = block.input) !== null && _a !== void 0 ? _a : {}).length;
                if (size > PRUNE_BLOCK_MAX_CHARS) {
                    return Object.assign(Object.assign({}, block), { input: {
                            _omitido: `input grande (${size} caracteres) omitido do histórico; ` +
                                'o efeito desta chamada já está aplicado no documento.',
                        } });
                }
            }
            return block;
        });
        return Object.assign(Object.assign({}, m), { content });
    }
}
// ============================================================================
// Orçamento de CONTEXTO dentro do turno
// ----------------------------------------------------------------------------
// `pruneBulkyToolBlocks` roda só na fronteira do turno. DENTRO do turno cada
// `tool_result` se acumulava sem teto: no fluxo "Formatar" de documento grande,
// um ciclo ler+escrever custa ~24 mil tokens, e a janela de 200k do modelo
// estourava por volta do 14º passo — antes de `LIMIT_STEPS` sequer ser
// alcançado. O usuário pagava 14 passos e recebia um erro genérico do backend.
//
// A guarda tem dois estágios, nesta ordem:
//  1. PODAR as mensagens antigas do turno (mantendo intactas as últimas, que é
//     sobre o que o modelo age agora);
//  2. se ainda assim não couber, ENCERRAR o turno com um recado útil — o
//     trabalho já feito continua no documento e o "Desfazer" segue disponível.
//
// Por que podar só perto do limite, e não sempre: reescrever mensagens antigas
// muda o prefixo e ERRA o cache de conversa (ver `_with_conversation_cache` no
// backend). Num turno normal o cache vale mais; num turno que está prestes a
// morrer, sobreviver vale mais. Paga-se o cache só quando é preciso.
/** Janela de contexto do modelo. Os três tiers atuais têm 200k. */
export const CONTEXT_WINDOW_TOKENS = 200000;
/**
 * Reserva para a RESPOSTA. O backend pede `MAX_TOKENS` (8k) por passo; o dobro
 * cobre isso com folga para variação do provedor.
 */
export const OUTPUT_RESERVE_TOKENS = 16000;
/**
 * Teto do PROMPT INTEIRO — system, schemas das ferramentas, manual e conversa.
 * Diferente da versão anterior, que orçava só `messages` e descontava uma
 * estimativa fixa do resto: o número real vem do provedor (`usage`), e ele já
 * inclui tudo. Descontar de novo seria contar duas vezes.
 */
export const PROMPT_BUDGET_TOKENS = CONTEXT_WINDOW_TOKENS - OUTPUT_RESERVE_TOKENS;
/** A partir daqui a poda entra. Abaixo disso, o cache de conversa fica intacto. */
export const PRUNE_TRIGGER_TOKENS = Math.round(PROMPT_BUDGET_TOKENS * 0.7);
/**
 * Mensagens finais preservadas na poda: o `tool_use` do assistente e o
 * `tool_result` correspondente do último passo. Podá-los cegaria o modelo
 * justamente sobre o que ele acabou de pedir.
 */
export const KEEP_INTACT_MESSAGES = 2;
/**
 * Tamanho REAL do prompt do passo anterior, em tokens, conforme o provedor.
 *
 * É a âncora do orçamento — e existe porque estimar deu errado de um jeito caro:
 * anexo (PDF/imagem) viaja como base64, e contar esses caracteres como texto
 * inflava a conta de 4x a 26x. Um PDF de 22 páginas (~44 mil tokens de verdade)
 * era medido em 194 mil e o turno morria na primeira rodada, antes de fazer
 * qualquer trabalho. O provedor sabe a resposta; basta perguntar.
 *
 * Soma as três parcelas porque `input_tokens` NÃO inclui o que veio do cache —
 * olhar só ele subestimaria justamente as conversas longas, que são o caso que
 * o orçamento existe para proteger.
 *
 * `null` quando o provedor não reportou nada (host antigo, erro parcial): aí o
 * chamador não tem base para julgar e não deve bloquear.
 */
export function promptTokensFromUsage(usage) {
    var _a, _b, _c;
    if (!usage)
        return null;
    const soma = ((_a = usage.input_tokens) !== null && _a !== void 0 ? _a : 0) +
        ((_b = usage.cache_read_input_tokens) !== null && _b !== void 0 ? _b : 0) +
        ((_c = usage.cache_creation_input_tokens) !== null && _c !== void 0 ? _c : 0);
    return soma > 0 ? soma : null;
}
/**
 * Quanto texto (em tokens) um conjunto de blocos acrescenta ao prompt.
 *
 * Usada para o DELTA entre um passo e o seguinte — o que o cliente vai anexar e
 * o provedor ainda não contou. Aproxima por caracteres (~3,6 por token em
 * pt-BR), o que serve bem para `tool_result` e texto.
 *
 * Payload `base64` de imagem/PDF é DESCARTADO da contagem: são milhões de
 * caracteres que o modelo não processa como texto. No lugar entra uma
 * estimativa por bloco (`MEDIA_BLOCK_TOKENS`), grosseira de propósito — a
 * medida que vale é a do provedor, esta só evita que uma mídia passe por zero.
 */
const MEDIA_BLOCK_TOKENS = 2000;
export function estimateContextTokens(messages) {
    let chars = 0;
    let midias = 0;
    const varrer = (valor) => {
        var _a;
        if (valor === null || valor === undefined)
            return;
        if (Array.isArray(valor)) {
            valor.forEach(varrer);
            return;
        }
        if (typeof valor === 'object') {
            const obj = valor;
            // `{ type: 'base64', media_type, data }` — o `data` é o que não conta.
            if (obj.type === 'base64' && typeof obj.data === 'string') {
                midias++;
                chars += String((_a = obj.media_type) !== null && _a !== void 0 ? _a : '').length;
                return;
            }
            for (const v of Object.values(obj))
                varrer(v);
            return;
        }
        chars += String(valor).length;
    };
    varrer(messages);
    return Math.ceil(chars / 3.6) + midias * MEDIA_BLOCK_TOKENS;
}
/** Poda as mensagens ANTIGAS do turno, preservando as `keepIntact` últimas. */
export function pruneOlderTurnMessages(messages, keepIntact = KEEP_INTACT_MESSAGES) {
    const corte = Math.max(0, messages.length - keepIntact);
    return messages.map((m, i) => (i < corte ? pruneMessageBlocks(m) : m));
}
